import {
  Box,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { capitalize, isEmpty, isNil, map, toLower } from 'lodash';
import visuallyHidden from '@mui/utils/visuallyHidden';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import ShipByDateCell from 'common/components/ShipByDateCell';
import { utcToZonedTime } from 'date-fns-tz';

import {
  getReviewInClaimEligibilityLineItemsByPage,
  sortReviewInClaimEligibilityLineItemsWorkQueue,
} from 'store/thunks/workQueueThunks';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';

import { SortKey, SortOrder } from 'enums/common.enum';

const headCells = [
  {
    id: SortKey.PATIENT_NAME,
    label: 'Patient Name',
  },
  {
    id: SortKey.PARTNER_NAME,
    label: 'Partner Name',
  },
  {
    id: SortKey.REQUESTED_DATE,
    label: 'Requested Date',
  },
  {
    id: SortKey.NEEDS_BY_DATE,
    label: 'Needs By Date',
  },
];

const ReviewInClaimEligibilityTable = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { sortBy, pages, currentPage } = useSelector(
    ({ workQueue }) => workQueue.reviewInClaimEligibility
  );

  const totalPages = useSelector(({ workQueue }) =>
    Math.ceil(workQueue.reviewInClaimEligibility.count / 25)
  );
  const lineItems = pages[currentPage];

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      sortReviewInClaimEligibilityLineItemsWorkQueue({
        sortBy: {
          sortKey,
          sortOrder:
            sortBy.sortKey === sortKey && sortBy.sortOrder === SortOrder.ASCENDING
              ? SortOrder.DESCENDING
              : SortOrder.ASCENDING,
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      // page did not change, do not do anything.
      return;
    }

    dispatch(getReviewInClaimEligibilityLineItemsByPage({ page }));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Grid container direction='column'>
      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={
                      sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : false
                    }
                  >
                    <TableSortLabel
                      active={sortBy.sortKey === headCell.id}
                      direction={sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'}
                      onClick={handleSort}
                      data-head-property={headCell.id}
                    >
                      {headCell.label}
                      {sortBy.sortKey === headCell.id ? (
                        <Box component='span' sx={visuallyHidden}>
                          {toLower(sortBy.sortOrder) === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {map(lineItems, (lineItem) => (
                <TableRow key={lineItem.requestLineItemId} hover>
                  <TableCell component='th' scope='row' align='left'>
                    <Link
                      to={`/patients/${lineItem.mpi}`}
                      style={{ color: theme.palette.text.primary }}
                    >
                      {lineItem.patientName}
                    </Link>
                  </TableCell>
                  <TableCell>{capitalize(lineItem.partnerName)}</TableCell>
                  <TableCell>
                    {format(utcToZonedTime(new Date(lineItem.requestedDate), 'utc'), 'MM/dd/yyyy')}
                  </TableCell>
                  <TableCell>
                    <ShipByDateCell shipByDate={lineItem.shipByDate} tags={lineItem.tags} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {!isNil(lineItems) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={6} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(lineItems) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(lineItems) && isEmpty(lineItems) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default ReviewInClaimEligibilityTable;
